import { DocumentIcon, PhotoIcon, PlayCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { format } from "date-fns";
import React, { useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

/**
 * Just the fields the click handler needs to build the pinned-note
 * payload for the video overlay. Structural so it accepts both the
 * full `ChatMessageType` (from `useGetChatMessagesQuery`) and the lean
 * `GetChatMediaQuery` node — neither query needs to win.
 */
interface MultimediaSourceMessage {
  userFirstName?: string | null;
  userLastName?: string | null;
  content?: string | null;
  createdDate: string;
}

export type MultimediaItem = {
  key: string;
  kind: "video" | "image" | "file";
  mediaId: string | null;
  thumbnailUrl: string | null;
  /** Full-size URL for images — used by the lightbox modal preview. */
  imageUrl?: string | null;
  title: string;
  createdDate: string;
  sourceMessage: MultimediaSourceMessage;
};

interface MultimediaCardProps {
  item: MultimediaItem;
  onClick: () => void;
}

export const MultimediaCard: React.FC<MultimediaCardProps> = ({ item, onClick }) => {
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState<boolean>(false);

  const formattedDate = (() => {
    try {
      return format(new Date(item.createdDate), "MMM d, yyyy");
    } catch (e) {
      console.error("[MultimediaCard] Failed to format date:", item.createdDate, e);
      return "";
    }
  })();

  const isClickable = (item.kind === "video" && !!item.mediaId) || (item.kind === "image" && !!item.imageUrl);

  // Fallback icon when there's no thumbnail URL OR the image failed to
  // load (e.g. CORS-blocked, expired signed URL, unsupported format).
  const showThumbnailImage = !!item.thumbnailUrl && !thumbnailLoadFailed;
  const FallbackIcon = item.kind === "video" ? PlayCircleIcon : item.kind === "image" ? PhotoIcon : DocumentIcon;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      className={clsx(
        "flex flex-row gap-2 items-start p-2 rounded-lg border border-limestone-200 bg-white text-left w-full",
        isClickable && "hover:bg-limestone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400",
        !isClickable && "cursor-default",
      )}
    >
      {/* Thumbnail */}
      <div className="relative h-8 w-8 rounded bg-limestone-200 shrink-0 overflow-hidden flex items-center justify-center">
        {showThumbnailImage ? (
          <img
            src={item.thumbnailUrl!}
            alt=""
            className="h-full w-full object-cover"
            onError={() => {
              console.error("[MultimediaCard] Thumbnail failed to load:", item.thumbnailUrl);
              setThumbnailLoadFailed(true);
            }}
          />
        ) : (
          <FallbackIcon className="h-4 w-4 text-limestone-600" />
        )}
        {/* Play-icon overlay for videos so users can spot them at a glance
            (kept even on top of the thumbnail image). */}
        {item.kind === "video" && showThumbnailImage ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <PlayCircleIcon className="h-4 w-4 text-white" />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        <Paragraph size="extra-small" displayType="loud" colorType="primary" className="truncate">
          {item.title}
        </Paragraph>
        <Paragraph size="extra-small" colorType="secondary">
          {formattedDate}
        </Paragraph>
      </div>
    </button>
  );
};
